import { useEffect, useState } from "react";
import { useInView } from "../hooks/useInView";

/**
 * Animated count-up. Uses requestAnimationFrame and an ease-out curve.
 * Triggers when the element scrolls into view.
 *
 * Props:
 *  - end:      final value (number)
 *  - duration: animation duration ms (default 1800)
 *  - prefix / suffix
 *  - decimals: number of decimals (default 0)
 */
export const CountUp = ({
  end,
  duration = 1800,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
  "data-testid": testId,
}) => {
  const { ref, inView } = useInView({ threshold: 0.3 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      setValue(end * easeOut(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  const formatted =
    decimals > 0
      ? value.toFixed(decimals)
      : Math.round(value).toLocaleString("fr-FR");

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default CountUp;
